import classNames from 'classnames';
import { useBlockMetadata } from 'components/common-form/hooks/useBlockMetadata';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { orderVertically } from 'components/common-form/utils/ColumnUtils';
import { FC, ReactNode, useMemo } from 'react';
import type { ProductNode } from 'state/product-description/model';
import type { FieldDirection, Width } from 'state/product-description/types';
import type { BatchWorkspaceVersion, QuoteWorkspaceVersion } from 'state/userPreferences/userPreferences.model';
import type { WorkspaceType } from 'state/workspaces/model';

import { RenderFormNodeComponent } from '../../FormBuilder';
import { getClassNameFromWidth, getColumnCountForWidth } from './utils';

export interface FieldsListProps {
  nodes?: readonly ProductNode[];

  blockWidth: Width;

  direction: FieldDirection;

  renderChild: (node: ProductNode, width: Width) => ReactNode;
}

export const ChildNode: FC<{
  tabId: string;
  node: ProductNode;
  level: number;
  defaultWidth: Width;
  workspaceType: WorkspaceType;
  quoteWorkspaceVersion: QuoteWorkspaceVersion;
  batchWorkspaceVersion: BatchWorkspaceVersion;
  mustForceAdvancedMode: boolean;
}> = ({ node, defaultWidth, level, ...tabProps }) => {
  const blockMetadata = useBlockMetadata(node.id);
  const metadata = useFieldMetadata(node.id) ?? blockMetadata;

  const width = useMemo(() => {
    const isMagnetNode = node.id === 'IsMagnet' || node.id === 'IsMagnetMin';

    if (isMagnetNode) {
      const w = node.width ?? defaultWidth;
      return metadata?.width ?? w;
    }
    return node.width ?? metadata?.width ?? defaultWidth;
  }, [defaultWidth, metadata?.width, node.width, node.id]);

  if (metadata?.isHidden) {
    return null;
  }

  if (
    metadata?.isForAdvancedMode &&
    tabProps.workspaceType === 'static' &&
    tabProps.quoteWorkspaceVersion === 'Standard' &&
    !tabProps.mustForceAdvancedMode
  ) {
    return null;
  }

  return (
    <FieldWrapper width={width} className={`field-${node.id}`}>
      <RenderFormNodeComponent level={level + 1} node={node} {...tabProps} />
    </FieldWrapper>
  );
};

export const FieldWrapper: FC<{
  children: ReactNode;
  className?: string;
  width: Width;
}> = ({ className, width, children }) => {
  const colClassName = getClassNameFromWidth(width);

  return <div className={classNames(colClassName, className)}>{children}</div>;
};

export const FieldsList: FC<FieldsListProps> = ({ nodes, blockWidth, direction, renderChild }) => {
  if (nodes === undefined) {
    return null;
  }
  const columnCount = getColumnCountForWidth(blockWidth ?? 'full');
  const children = direction === 'topToBottom' ? orderVertically<ProductNode>(nodes, columnCount) : nodes;

  if (children) {
    return <div className="row">{children.map(subChild => renderChild(subChild, blockWidth))}</div>;
  }
  return null;
};

FieldsList.displayName = 'FieldsList';
